<template>
  <div>
    <advanced-table
      :headers="headers"
      :filters="filters"
      :sortBy="'callFirst'"
      :descending="true"
      :resource="resource"
      :optionLists="optionLists"
    />
    <partner-check-dialog
      v-model="checkDialog"
      :partner="selectedPartner"
      @checked="onPartnerChecked"
    />
  </div>
</template>

<script>
import AdvancedTable from "@/components/AdvancedTable.vue";
import PartnerCheckDialog from "@/components/events/PartnerCheckDialog.vue";
import { format, differenceInCalendarDays } from "date-fns";

export default {
  data() {
    return {
      checkDialog: false,
      selectedPartner: null,
      headers: [
        {
          text: "שם",
          value: "name",
          align: "right",
          visible: true,
          width: "200",
          displayFunc: (data, key) => {
            return data[key] || 'ללא שם';
          },
          linkTo: item => {
            return `/partners/${item._id}`;
          }
        },
        {
          text: "תאריך יצירה",
          value: "created",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            return format(data[key], "DD-MM-YYYY");
          }
        },
        {
          text: "סוגים",
          value: "types",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            let field = "title";
            let p = {};
            let array = data[key];
            if (Array.isArray(array) && array.length) {
              return array
                .map(item => {
                  return this.entityById("partnerTypes", item)[field];
                })
                .reduce((p, v) => p + ", " + v);
            }
            return "";
          }
        },
        {
          text: "מדינות",
          value: "countries",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            let field = "name";
            let p = {};
            let array = data[key];
            if (Array.isArray(array) && array.length) {
              return array
                .map(item => {
                  let country = this.entityById("countries", item)
                  return country ? country[field] : 'ללא שם';
                })
                .reduce((p, v) => p + ", " + v);
            }
            return "";
          }
        },
        {
          text: "איזורים",
          value: "regions",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            let field = "name";
            let p = {};
            let array = data[key];
            if (Array.isArray(array) && array.length) {
              return array
                .map(item => {
                  return this.entityById("regions", item)[field];
                })
                .reduce((p, v) => p + ", " + v);
            }
            return "כל האיזורים";
          }
        },
        {
          text: "תאריך אימות פרטים",
          value: "lastValidation",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            return format(data[key], "DD-MM-YYYY");
          }
        },
        {
          text: "בדיקה",
          value: "checkDays",
          sortValue: "lastValidation",
          align: "right",
          visible: true,
          type: "CLICK",
          displayFunc: (data) => {
            return this.getCheckLabel(data);
          },
          click: (partner) => {
            this.openCheckDialog(partner);
          }
        },
        {
          text: "סוג הקשר",
          value: "relationship",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            let relationship = this.entityById("partnerRelationships", data[key]);
            return relationship ? relationship.title : 'לא ידוע';
          }
        },
        {
          text: "מומלץ",
          value: "recommended",
          align: "right",
          visible: true,
          type: 'ICON',
          displayFunc: (data, key) => {
            return data[key] ? { icon: 'star', color: 'orange' } : { icon: 'mdi-star-outline', color: 'gray' };
          }
        },
        {
          text: "להתקשר ראשון",
          value: "callFirst",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            return data[key] ? "כן" : "לא";
          }
        }
      ],
      filters: [
        {
          type: "COMBOBOX",
          label: "מדינות",
          model: "countries",
          items: "countries",
          itemText: "name",
          itemValue: "_id",
          active: true
        },
        {
          type: "COMBOBOX",
          label: "סוגי ספקים",
          model: "types",
          items: "types",
          itemText: "titlePlural",
          itemValue: "_id",
          active: true
        },
        {
          type: "DATE",
          label: "נוצר אחרי",
          model: "createdAfter",
          active: false
        },
        {
          type: "DATE",
          label: "נוצר לפני",
          model: "createdBefore",
          active: false
        },
        {
          type: "DATE",
          label: "אימות פרטים לפני",
          model: "lastValidationBefore",
          active: false
        },
        {
          type: "TEXT",
          label: "חיפוש חופשי",
          model: "search",
          icon: "search",
          expanded: true,
          active: true
        }
      ]
    };
  },
  computed: {
    partners() {
      return this.$store.getters["entityList"]("partners");
    },
    relationships() {
      return this.$store.getters["entityList"]("partnerRelationships");
    },
    types() {
      return this.$store.getters["entityList"]("partnerTypes");
    },
    countries() {
      return this.$store.getters["entityList"]("countries").filter(item => {
        return item.isActive;
      });
    },
    resource() {
      return this.$store.state.resources["partner"];
    },
    optionLists() {
      return {
        relationships: this.relationships,
        types: this.types,
        countries: this.countries
      }
    }
  },
  methods: {
    entityById(entityType, id) {
      return this.$store.getters["entity"](entityType, id);
    },
    getCheckLabel(partner) {
      if (!partner || !partner.lastValidation) {
        return "אף פעם";
      }
      const days = differenceInCalendarDays(
        new Date(),
        new Date(partner.lastValidation)
      );
      if (isNaN(days)) {
        return "אף פעם";
      }
      return String(Math.max(0, days));
    },
    openCheckDialog(partner) {
      this.selectedPartner = partner;
      this.checkDialog = true;
    },
    onPartnerChecked() {
      // AdvancedTable will refresh via entity-saved if needed;
      // force local label refresh from store entities.
      this.$forceUpdate();
    }
  },
  components: {
    AdvancedTable,
    PartnerCheckDialog
  }
};
</script>



// WEBPACK FOOTER //
// src/components/partners/Partners.vue